import { useMemo } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { FilestackImage } from 'components/Image';
import { BodySmall } from 'components/Text/BodySmall';
import { H4 } from 'components/Text/H4';
import { H5 } from 'components/Text/H5';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { getImageSrc } from 'utils/getImageSrc';

import {
  AmpCardsBlockImageSize,
  CmsAmpCardsBlockCardData,
} from './CmsAmpCardsBlock.types';

const ImageContainer = styled.div`
  margin-bottom: 24px;
  text-align: center;
  img {
    object-fit: contain;
  }
`;

type Props = {
  card: CmsAmpCardsBlockCardData;
  imageSize?: AmpCardsBlockImageSize | null | undefined;
  maxLength?: number;
};

export function CmsAmpCardsBlockCard({
  card,
  imageSize,
  maxLength = 3,
}: Props) {
  const url = useMemo(() => {
    if (card.url) return card.url;
    if (card.urls) return card.urls[CURRENT_LOCALE];

    return null;
  }, [card.url, card.urls]);

  return (
    <>
      {card.image &&
        (imageSize && imageSize === 'SMALL' ? (
          <ImageContainer>
            <FilestackImage
              data={card.image}
              alt={card.headline || ''}
              height={80}
              width={200}
            />
          </ImageContainer>
        ) : (
          <Box
            width={maxLength === 3 ? 240 : 180}
            mx="auto"
            mb={24}
            height={maxLength === 3 ? 240 : 180}
            borderRadius={
              imageSize && imageSize === 'LARGE_RECTANGULAR' ? undefined : '50%'
            }
            backgroundImage={`url(${getImageSrc({
              handle: card.image.handle,
              mimetype: card.image.mimetype,
            })})`}
            backgroundSize="cover"
            backgroundPosition="center"
          />
        ))}

      {card.label && (
        <HeadingSmall
          mt={0}
          mb="8px"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {card.label}
        </HeadingSmall>
      )}

      {card.headline && (
        <H4 mt={0} mb={16} lineHeight={1.375}>
          {url ? <a href={url}>{card.headline}</a> : card.headline}
        </H4>
      )}

      {card.subheadline && (
        <H5 mt={0} mb={16} lineHeight={1.375}>
          {card.url ? (
            <a href={card.url}>{card.subheadline}</a>
          ) : (
            card.subheadline
          )}
        </H5>
      )}

      {card.text && (
        <BodySmall
          lineHeight={1.5}
          mt={0}
          mb={16}
          dangerouslySetInnerHTML={{
            __html: card.text,
          }}
        />
      )}

      {card.body && (
        <BodySmall lineHeight={1.5} mt={0}>
          {card.body}
        </BodySmall>
      )}
    </>
  );
}
